import _ from 'underscore';
import RouteModel from './RouteModelv2';
import { Leg, LegTransfer } from '@fr/vue/types';
import { RouteRecomendation } from '@entities/route';
import { minutesToHours, isAfter } from 'date-fns';
type TsorkBykey = 'fullPrice';

type RecomendationRoutes = {
    cheap: RouteModel | null,
    direct: RouteModel | null,
    connection: RouteModel | null,
};

export default class RoutesModel {
    private _routesModels: RouteModel[];

    private recomendationRoutes: RecomendationRoutes | null = null;

    constructor(routes: RouteModel[]) {
        this._routesModels = routes;
    }

    initRecomendations() {
        this.recomendationRoutes = this.getRecomendationRoutes();
        this.recomendationRoutes.cheap?.setRecomendation(RouteRecomendation.Cheap);
        this.recomendationRoutes.direct?.setRecomendation(RouteRecomendation.Direct);
        this.recomendationRoutes.connection?.setRecomendation(RouteRecomendation.Connection);
    }
    getRecomendationRoutes(): RecomendationRoutes {

        const sortedRoutes =  new RoutesModel(this._routesModels).sortBy('fullPrice').value();

        const getRoutePriority = (cheapRoute: RouteModel | null, route: RouteModel, params?: { onlyEarlyFlight?: boolean }): RouteModel | null => {
            const totalTime = route.there?.routeTime || 0;
            const cheapTotalTime = cheapRoute?.there?.routeTime || 0;
            const equalPrice = cheapRoute?.fullPrice === route.fullPrice;

            if(!equalPrice) return cheapRoute;

            const equalRouteTime = cheapTotalTime === totalTime;
            const cheapDeparute = new Date(cheapRoute?.there?.legs?.[0].departureDate || new Date());
            const deaparture = new Date(route?.there?.legs?.[0].departureDate || new Date());
            const equalDirect = cheapRoute?.isDirect === route.isDirect;

            if(params?.onlyEarlyFlight && equalDirect && equalPrice && isAfter(cheapDeparute, deaparture)) {
                return route;
            }

            if(equalDirect && equalPrice && cheapTotalTime > totalTime) {
                return route;
            }
            if(equalDirect && equalPrice && equalRouteTime && isAfter(cheapDeparute, deaparture)) {
                return route;
            } 
            return cheapRoute;

        };

        const isRecomendationConnectionTime = (route: RouteModel | undefined | null) => {
            const thereLegs = route?.there?.legs;
            const backLegs = route?.back?.legs;

            return [thereLegs, backLegs].every((legs) => {
                const transfer: LegTransfer | null | undefined = legs?.[1]?.transfer;
                const connectionHours = (transfer?.waitingTimeBeforeFlight || 0) / 60;
                const isRecomendationTime = connectionHours >= 3 && connectionHours <= 5; 
                const isValid = legs?.length === 2 && isRecomendationTime;
                return isValid;
            })
        } 

        const getCheapRoute = (routes: RecomendationRoutes, route: RouteModel): RouteModel | null => {
            if(!routes.cheap) {
                return route;
            }
            if(routes.cheap && routes.cheap.fullPrice === route.fullPrice && isRecomendationConnectionTime(route)) {
                return isRecomendationConnectionTime(routes.cheap) ? getRoutePriority(routes.cheap, route) : route;
            }
            return getRoutePriority(routes.cheap, route);
        }

        const getDirectRoute = (routes: RecomendationRoutes, route: RouteModel): RouteModel | null => {
            if(routes.cheap?.isDirect) {
                return null;
            }
            if(!routes.direct && route.isDirect) {
                return route;
            }

            return getRoutePriority(routes.direct, route, { onlyEarlyFlight: true });
        }

        const getConnectionRoute = (routes: RecomendationRoutes, route: RouteModel): RouteModel | null => {
            if(routes.cheap?.isDirect) {
                return null;
            }
            if(route.isDirect) return routes.connection;
            const hasCheapRecomendationConnection = isRecomendationConnectionTime(routes.cheap);
            if(routes.cheap && hasCheapRecomendationConnection) {
                return null;
            }
            if(routes.cheap?.pk === route.pk) {
                return routes.connection;
            }
            const isRecomendationTime = isRecomendationConnectionTime(route);
            if(!routes.connection && isRecomendationTime) {
                return route;
            }
            return isRecomendationTime ? getRoutePriority(routes.connection, route) : routes.connection;
        }
        const recomendationRoutes = sortedRoutes.reduce<RecomendationRoutes>((acc, route, index) => {
            acc.cheap = getCheapRoute(acc, route);
            acc.direct = getDirectRoute(acc, route);
            acc.connection = getConnectionRoute(acc, route);
            return acc; 
        }, { cheap: null, direct: null, connection: null } as RecomendationRoutes);

        const { connection, direct } = recomendationRoutes;
        if(connection && direct && direct.fullPrice < connection.fullPrice) {
            recomendationRoutes.connection = null;
        }
        return recomendationRoutes;
    }

    value(): RouteModel[] {
        return this._routesModels;
    }

    sortBy(key: TsorkBykey = 'fullPrice', params?: { shouldSortRecomendation?: boolean }): RoutesModel {
        const sortedRoutes = _.sortBy(this._routesModels, (route: RouteModel) => {
            if(params?.shouldSortRecomendation && route.recomendation) {
                const priority: Record<RouteRecomendation, number> = {
                    [RouteRecomendation.Cheap]: 1,
                    [RouteRecomendation.Direct]: 2,
                    [RouteRecomendation.Connection]: 3,
                }
                return priority[route.recomendation];
            }
            return  route[key]
        });
        return new RoutesModel(sortedRoutes);
    }

    getUniqRoutes(): RoutesModel {
        if(this._routesModels.length < 10) return new RoutesModel(this._routesModels);

        const uniqRoutes = _.uniq(this._routesModels, (routeModel) => {
            const routePk = routeModel?.pk;
            const transferAvailable = routeModel.tourProduct?.transferType === 'none' ? '0' : '1';

            return `${routePk}_${transferAvailable}`;
        });

        return new RoutesModel(uniqRoutes);
    }
}
